import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import PageTransition from "@/components/PageTransition";
import { getHistory, getWallet, fmtAmount, shortAddr } from "@/lib/wallet";
import { ArrowDownToLine, ArrowUpFromLine, Sparkles, Wallet } from "lucide-react";

const TYPE = {
  stake:   { label: "Stake",        icon: ArrowDownToLine, color: "var(--evm)" },
  unstake: { label: "Unstake",      icon: ArrowUpFromLine, color: "var(--warning)" },
  claim:   { label: "Claim",        icon: Sparkles,        color: "var(--success)" },
  connect: { label: "Wallet linked",icon: Wallet,          color: "var(--text-2)" },
};

export default function History() {
  const [items, setItems] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setItems(getHistory());
    setWallet(getWallet());
  }, []);

  const filtered = filter === "all" ? items : items.filter(i => i.type === filter);

  return (
    <PageTransition>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20">
        <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ Transaction log</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">History</h1>
        <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--text-2)" }}>
          Every stake, unstake and reward claim is anchored to the validator mempool.
        </p>

        <div className="flex items-center gap-2 mt-8 border-b pb-3" style={{ borderColor: "var(--border)" }}>
          {[
            { id: "all",     label: "All" },
            { id: "stake",   label: "Stake" },
            { id: "unstake", label: "Unstake" },
            { id: "claim",   label: "Claim" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`history-filter-${t.id}`}
              className="px-3 py-1 text-xs uppercase tracking-wider transition-colors"
              style={{
                background: filter === t.id ? "#fff" : "transparent",
                color:      filter === t.id ? "#000" : "var(--text-2)",
              }}
            >{t.label}</button>
          ))}
          <div className="ml-auto text-xs font-mono" style={{ color: "var(--text-3)" }}>
            {wallet ? shortAddr(wallet.address) : "Not connected"}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 text-center text-sm" style={{ color: "var(--text-3)" }}>
            No transactions yet. Open a position to populate your log.
          </div>
        ) : (
          <table className="w-full mt-6 text-sm" data-testid="history-table">
            <thead>
              <tr className="text-left" style={{ color: "var(--text-3)" }}>
                <th className="pb-3 text-[10px] uppercase tracking-widest font-medium">Type</th>
                <th className="pb-3 text-[10px] uppercase tracking-widest font-medium">Asset</th>
                <th className="pb-3 text-[10px] uppercase tracking-widest font-medium text-right">Amount</th>
                <th className="pb-3 text-[10px] uppercase tracking-widest font-medium">Hash</th>
                <th className="pb-3 text-[10px] uppercase tracking-widest font-medium text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tx) => {
                const meta = TYPE[tx.type] || TYPE.stake;
                const Icon = meta.icon;
                return (
                  <tr key={tx.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                    <td className="py-4">
                      <span className="inline-flex items-center gap-2">
                        <Icon size={13} style={{ color: meta.color }} />
                        <span style={{ color: meta.color }}>{meta.label}</span>
                      </span>
                    </td>
                    <td className="py-4 font-mono">{tx.token || "—"}</td>
                    <td className="py-4 font-mono text-right">{tx.amount ? fmtAmount(tx.amount) : "—"}</td>
                    <td className="py-4 font-mono text-xs" style={{ color: "var(--text-3)" }}>
                      {tx.hash ? shortAddr(tx.hash) : "—"}
                    </td>
                    <td className="py-4 font-mono text-right text-xs" style={{ color: "var(--text-3)" }}>
                      {new Date(tx.timestamp).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </PageTransition>
  );
}
